import { useRef, useEffect } from "react";
import ttsManifest from "./tts-manifest.json";

// 2-letter ISO 639-1 keys, matching the literal `lang` strings speak()/speakFallback() callers
// pass (e.g. 'tr' — see LessonView.js/FlashcardView.js/PlacementTestView.js) — the SAME string
// is also used as the ttsManifest[lang] lookup key, so this map and the manifest never diverge
// in practice. 'en' and 'fa' were missing: both are actively-used BASE_LANGS (constants.js) base
// languages, and their absence silently fell through to the 'tr-TR' default below, which (via
// the `voices.find(v => v.lang === bcp47)` exact-match clause) can wrongly win over a real
// English/Farsi voice instead of correctly falling through to the startsWith(lang) fallback.
export const LANG_TO_BCP47 = { tr: 'tr-TR', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', en: 'en-US', fa: 'fa-IR' };

// Maps a language's full English display name (e.g. "Spanish", as stored in
// profile.baseLanguage / profile.targetLanguage, matching src/i18n/index.js's STATIC bundle
// keys — NOT a 2-letter code) to a BCP-47 tag. A separate map from LANG_TO_BCP47 above rather
// than a re-keying of it. Used by Babi voice (useVoiceTurn.js) to pick Speech-to-Text /
// Text-to-Speech language codes for BOTH the base language (chat widget) and the target
// language (Listen & Speak Lab). Covers exactly the 12 statically-supported languages; any
// other value (e.g. a legacy/custom EXTRA_LANGS pick with no static bundle) falls back at the
// call site — STT/TTS will misbehave for that rare case until a real mapping is added.
export const LANGUAGE_NAME_TO_BCP47 = {
  English: 'en-US', Farsi: 'fa-IR', Arabic: 'ar-SA', French: 'fr-FR', German: 'de-DE',
  Spanish: 'es-ES', Russian: 'ru-RU', Portuguese: 'pt-PT', Chinese: 'cmn-Hans-CN',
  Turkish: 'tr-TR', Hindi: 'hi-IN', Italian: 'it-IT',
};

// The BCP-47 code for the learner's TARGET (course) language — what Listen & Speak Lab
// recognises and speaks, the opposite of the chat widget which works in the base language.
// Sourced from profile.targetLanguage (a full English name, e.g. "Turkish"; App.js guarantees
// it's set) so it generalises to a future non-Turkish course without a hardcoded "tr-TR"
// literal at the call site. Reuses the name→BCP-47 map above (keyed by display name, and it
// already contains Turkish); "tr-TR" is only the last-ditch fallback.
export function getTargetLanguageBcp47(profile) {
  return LANGUAGE_NAME_TO_BCP47[profile?.targetLanguage || 'Turkish'] || 'tr-TR';
}

// Listen & Speak Lab's dedicated TTS voice, per target language — a warm female Chirp3-HD
// voice (Google's newest tier), deliberately distinct from the rest of the app's
// tr-TR-Wavenet-E (male). Passed explicitly on the Lab's own TTS calls only, never a
// catalog-wide override. Turkish pick: 'Achernar' ("Soft" per Google's Chirp3-HD docs),
// chosen by the product owner from an audition of all 14 female Chirp3-HD tr-TR voices
// (data/lab-voice-samples/). Returns null for a language with no chosen Lab voice yet — the
// Lab then falls back to the proxy's best-available-voice pick.
const LAB_TTS_VOICE_BY_TARGET = { Turkish: 'tr-TR-Chirp3-HD-Achernar' };
export function getLabTtsVoice(profile) {
  return LAB_TTS_VOICE_BY_TARGET[profile?.targetLanguage || 'Turkish'] || null;
}

// Maps our internal lowercase langCode (profile.targetLanguage.toLowerCase(), the same
// currency used by getGrammarPoints/getCurriculum elsewhere) to the 2-letter code speak()/
// speakFallback() expect (LANG_TO_BCP47's keys, above). Only Turkish exists today; a future
// target language adds one entry here — every speak() call site below already derives its
// langCode from profile, so no call site needs to change again.
const LANGCODE_TO_SPEAK_LANG = { turkish: 'tr' };
export function getSpeakLangCode(langCode) {
  return LANGCODE_TO_SPEAK_LANG[langCode?.toLowerCase()] || 'tr';
}

export function speakFallback(text, lang = 'tr') {
  if (!window.speechSynthesis) return;
  const bcp47 = LANG_TO_BCP47[lang] || 'tr-TR';
  const run = () => {
    const voices = window.speechSynthesis.getVoices();
    const u = new SpeechSynthesisUtterance(text);
    const v = voices.find(v => v.lang === bcp47) || voices.find(v => v.lang.startsWith(lang));
    if (v) u.voice = v;
    u.lang = bcp47; u.rate = 0.85;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };
  if (window.speechSynthesis.getVoices().length) {
    run();
  } else {
    window.speechSynthesis.addEventListener("voiceschanged", run, { once: true });
    setTimeout(run, 500);
  }
}

// speak(text, wordId?, lang?, slot?)
// Only ever plays pre-generated audio via the exact-text manifest lookup: it's
// content-hash keyed, so a curriculum text edit naturally produces a cache miss
// instead of ever serving another sentence's audio under the same key. wordId/
// slot are intentionally unused for lookup — generate-tts.mjs's word_id+slot
// entries hold whichever curriculum text was last synthesized for that slot, so
// once curriculum.js's example/word text changes, that entry goes stale until
// generate-tts.mjs is re-run, and playing it back would say the wrong sentence.
// Better to fall through to the Web Speech API (still correct, just less polished)
// than to risk mismatched WaveNet audio. wordId/lang/slot are kept in the
// signature for call-site compatibility even though only `lang` is used.
export function speak(text, wordId, lang, slot) {
  const textUrl = typeof ttsManifest[text] === 'string' ? ttsManifest[text] : null;
  if (textUrl) {
    const audio = new Audio(textUrl);
    audio.play().catch(() => speakFallback(text, lang || 'tr'));
    return;
  }
  speakFallback(text, lang || 'tr');
}

export function useWakeLock() {
  const lockRef = useRef(null);

  const acquire = async () => {
    if (!navigator.wakeLock) return;
    try {
      lockRef.current = await navigator.wakeLock.request("screen");
    } catch { /* unsupported or permission denied — silently ignore */ }
  };

  useEffect(() => {
    acquire();
    // Re-acquire if the user returns to the app after switching away
    const onVisible = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      lockRef.current?.release().catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
